import { HStack, Text, VStack } from '@chakra-ui/react';
import type { DisplayRightsProposalSummary } from '@fluenceur/types';
import { Bell, Pause, Prohibition } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { resolveDisplayRightsExpiry } from './display-rights-status';

interface DisplayRightsExpiryNoticeProps {
  proposal: DisplayRightsProposalSummary | null;
}

/**
 * What happens at the deadline, said before it arrives.
 *
 * Shown once the brand has actually been warned, so the panel and the
 * notification centre tell the same story rather than two overlapping ones. The
 * two lines under it are the point: an expiry PAUSES the boost, it never deletes
 * anything, and it blocks new launches until the rights are renewed. Stating
 * both up front is what stops the pause from being read later as a failure.
 */
export function DisplayRightsExpiryNotice({ proposal }: DisplayRightsExpiryNoticeProps) {
  const { t } = useTranslation();
  const expiry = resolveDisplayRightsExpiry(proposal);

  if (!expiry.noticeSent) return null;

  return (
    <VStack
      align="stretch"
      gap={2}
      px={3}
      py={2.5}
      mt={2}
      border="1px solid"
      borderColor="orange.200"
      rounded="lg"
      bg="orange.50"
      data-testid="display-rights-expiry-notice"
    >
      <HStack gap="1.5" align="start">
        <Bell width={12} height={12} color="currentColor" />
        <Text fontSize="11px" color="orange.700">
          {t('displayRights.expiry.notice')}
        </Text>
      </HStack>

      <VStack align="stretch" gap={1} borderTop="1px solid" borderColor="orange.200" pt={2}>
        <HStack gap="1.5" align="start">
          <Pause width={12} height={12} color="currentColor" />
          <Text fontSize="11px" color="orange.700">
            {t('displayRights.expiry.atTerm.paused')}
          </Text>
        </HStack>
        <HStack gap="1.5" align="start">
          <Prohibition width={12} height={12} color="currentColor" />
          <Text fontSize="11px" color="orange.700">
            {t('displayRights.expiry.atTerm.blocked')}
          </Text>
        </HStack>
      </VStack>
    </VStack>
  );
}
